// Third party components
import React from 'react';
import PropTypes from 'prop-types';

//MUI components
import { Divider, Typography, Card, CardContent, Container } from '@mui/material';

//Own components
import Tabber from '../../components/Tabber';
import TabberPanel from '../../components/TabberPanel';
import DetailsExplorer from '../../components/DetailsExplorer';

// helpers
import { isListEmpty } from 'helpers';

// Tabs are defined here for the sub header
const tabs = [{ index: 0, label: 'Practices' }, { index: 1, label: 'All Activities' }];

// Details config for fields
const detailsConfigActivities = [
  { order: 0, label: 'Activity Name', field: 'activityName' },
  { order: 1, label: 'Requested Date', field: 'requestedDate', isDate: true },
  { order: 2, label: 'Activity Category', field: 'activityCategory' },
  { order: 3, label: 'Planned Start Date', field: 'plannedStartDate', isDate: true },
  { order: 4, label: 'Recipient Name', field: ['individualRecipientName', 'organizationRecipientName'] },
  { order: 5, label: 'Started Date', field: 'startedDate', isDate: true },
  { order: 6, label: 'Status', field: 'status' },
  { order: 7, label: 'Completed Date', field: 'completedDate', isDate: true },
]

// Main component
const PracticesAndActivitiesCard = ({ practices, activities, loading }) => {
  const practicesMerged = [...practices.practiceLines, ...practices.practicePoints, ...practices.practicePolygons];
  const sortedPractices = practicesMerged.sort((a, b) => a.practiceName?.localeCompare(b.practiceName));

  const practiceActivities = sortedPractices ? sortedPractices.filter(p => p.activities.length > 0).map(p => p.activities).flat() : [];
  const allActivities = practiceActivities && activities ? [...practiceActivities, ...activities] : practiceActivities || activities;
  const sortedActivities = allActivities?.sort((a, b) => a.activityName?.localeCompare(b.activityName));

  const [tabIndex, setTabIndex] = React.useState(0);

  // Function for handling the tab change
  const handleChange = async (newValue) => {
    setTabIndex(newValue);
  };

  return (
    <>
      <Container maxWidth={false} sx={{ marginTop: 2, marginBottom: 2 }}>
        <Card>
          <CardContent>
            <Typography variant="sectionHeader">In This Unit</Typography>
          </CardContent>
          <Divider />
          <CardContent>
            <Tabber
              index={tabIndex}
              tabs={tabs}
              onChange={handleChange}
            />
            <Divider />

            {/* 🌏 Main content: Practices List on left, Selected Practice Details on right */}
            <TabberPanel value={tabIndex} index={0}>
              {isListEmpty(sortedPractices) ?
                (<>No mapped Practices</>) :
                <DetailsExplorer
                  items={sortedPractices}
                  loading={loading}
                  singularName="Practice"
                  pluralName="Practices"
                  nameField="practiceName"
                  parentDetailsSingularName="Practice"
                  parentDetailsNameField="practiceName"
                  parentDetailsGlobalIdNameField="globalid"
                  detailsConfig={detailsConfigActivities}
                />
              }
            </TabberPanel>
            {/* 🌏 Main content: All Activities List on left, Selected Activity Details (and Parent Practice Details) on right */}
            <TabberPanel value={tabIndex} index={1}>
              {isListEmpty(sortedActivities) ?
                (<>No Activities</>) :
                <DetailsExplorer
                  items={sortedActivities}
                  loading={loading}
                  singularName="Activity"
                  pluralName="Activities"
                  nameField="activityName"
                  parentDetailsSingularName="Practice"
                  parentDetailsNameField="practiceName"
                  parentDetailsGlobalIdNameField="practiceGlobalId"
                  detailsConfig={detailsConfigActivities}
                />
              }
            </TabberPanel>

          </CardContent>
        </Card>
      </Container>
    </>
  );
};

PracticesAndActivitiesCard.propTypes = {
  practices: PropTypes.objectOf(
    PropTypes.shape({
      practicePolygons: PropTypes.array,
      practiceLines: PropTypes.array,
      practicePoints: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    })
  ),
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      activityCategory: PropTypes.string,
      activityId: PropTypes.string,
      activityLineId: PropTypes.string,
      activityName: PropTypes.string,
      activityPointId: PropTypes.string,
      activityPolygonId: PropTypes.string,
      activityUnitId: PropTypes.string,
      completedDate: PropTypes.number,
      globalid: PropTypes.string,
      id: PropTypes.number,
      individualRecipientName: PropTypes.string,
      organizationRecipientName: PropTypes.string,
      plannedStartDate: PropTypes.number,
      practiceName: PropTypes.string,
      requestedDate: PropTypes.number,
      startedDate: PropTypes.number,
      status: PropTypes.string,
      survey123Url: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
}

export default PracticesAndActivitiesCard;
